import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePriceGroup } from 'core/contexts/priceGroup';
import { BookingOrigin } from '@utriper/core';
import { Field, Form, FormSpy } from 'react-final-form';
import { SelectComponent } from '@utriper/core-ui';
import { Box, FormHelperText, Grid2 } from '@mui/material';
import Title from 'modules/Pricing/components/Title';
import PriceGroupGrid from 'modules/Pricing/components/PriceGroupGrid';
import { Label } from '../../styles';
import ActionButtons from '../ActionButtons';
import OtaPriceActivationAlert from 'modules/Pricing/components/OtaPriceActivationAlert';

const AddOriginForm = ({ priceGroupId, handleClose = () => {} }) => {
    const { t, i18n } = useTranslation();
    const translate = (key, options) => t(`price_group_id.add_origin_form.${key}`, options);
    const { addBookingOrigin, unitsPriceGroup, units, prices: rangePrices, bookingOrigins: usedOrigins, submittingPriceGroup, validatePrices } = usePriceGroup();

    const [submitted, setSubmitted] = useState(false);
    const [bookingOriginId, setBookingOriginId] = useState(null);
    const [prices, setPrices] = useState([]);

    const bookingOriginOptions = useMemo(
        () =>
            BookingOrigin.getValidOrigins().map((bkOrigin) => {
                const disabled = usedOrigins?.includes(bkOrigin) ?? false;
                const name = BookingOrigin.getTranslatedName(BookingOrigin.types, bkOrigin, i18n.language);

                return {
                    id: bkOrigin,
                    name,
                    disabled,
                };
            }),
        [i18n.language, usedOrigins]
    );

    // Load ranges
    useEffect(() => {
        if (!bookingOriginId) return;

        if (Array.isArray(rangePrices) && rangePrices?.length > 0) {
            const rangesMap = new Map();

            for (const { quantityFrom, quantityTo } of rangePrices) {
                const key = `${quantityFrom}-${quantityTo}`;

                if (!rangesMap.has(key)) {
                    let prices = [];

                    for (const unit of unitsPriceGroup ?? []) {
                        prices.push({
                            unitId: unit?.id,
                            price: 0,
                            bookingOriginId,
                        });
                    }

                    rangesMap.set(key, { quantityFrom, quantityTo, prices });
                }
            }

            const ranges = [...rangesMap.values()]?.map((range, idx) => ({ rowId: idx, ...range }));
            setPrices([...ranges]);
        }
    }, [bookingOriginId, rangePrices, unitsPriceGroup]);

    const handleSubmit = async ({ bookingOriginId }) => {
        await addBookingOrigin(priceGroupId, bookingOriginId, prices);
        handleClose();
    };

    const validateForm = (values) => {
        const errors = {};

        if (!Number.isInteger(values?.bookingOriginId) || !BookingOrigin.isValidId(values?.bookingOriginId)) {
            errors.bookingOriginId = translate('validate_form.required');
        }

        const priceErrors = validatePrices(prices, units);

        errors.prices = priceErrors;

        return errors;
    };

    const handleChangeForm = ({ values }) => {
        const { bookingOriginId } = values;
        if (!Number.isFinite(bookingOriginId)) return;

        setBookingOriginId(bookingOriginId);
    };

    return (
        <Form
            key={`price-group-form-origins-${priceGroupId}`}
            onSubmit={handleSubmit}
            validate={validateForm}
            render={({ handleSubmit, form, errors }) => {
                const hasPriceErrors = submitted && errors?.prices;
                const priceErrors = errors?.prices ?? [];

                return (
                    <form key={`price-group-form-origins-${priceGroupId}`} onSubmit={handleSubmit} noValidate>
                        <Box component="section" py={2}>
                            <Title title={translate('title')} description={translate('description')} />

                            {/* Form */}
                            <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px">
                                    <Label>{translate('form.booking_origin')}</Label>
                                    <Field name="bookingOriginId" component={SelectComponent} values={bookingOriginOptions} label={translate('form.booking_origin_label')} />
                                    <OtaPriceActivationAlert bookingOrigins={bookingOriginId ? [bookingOriginId] : []} sx={{ mt: 1 }} />
                                </Grid2>

                                {prices?.length > 0 && bookingOriginId && (
                                    <Box width="100%" maxHeight="640px" overflow="auto" mt={1}>
                                        <PriceGroupGrid
                                            data={{
                                                units: unitsPriceGroup,
                                                bookingOrigins: [bookingOriginId],
                                                prices,
                                            }}
                                            actions={{
                                                setPrices,
                                            }}
                                            config={{
                                                hideDefaultOrigin: true,
                                            }}
                                        />
                                    </Box>
                                )}

                                {/* Errors */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxHeight: '78px',
                                        overflowY: 'auto',
                                        mt: 2,
                                    }}
                                >
                                    {hasPriceErrors && priceErrors?.length > 0 && priceErrors.map((error) => <FormHelperText error>{error}</FormHelperText>)}
                                </Box>

                                <ActionButtons
                                    handleSubmit={(e) => {
                                        setSubmitted(true);
                                        return handleSubmit(e);
                                    }}
                                    handleClose={(e) => {
                                        handleClose();
                                        return form.reset(e);
                                    }}
                                    submitting={submittingPriceGroup}
                                    labelNext={translate('add_button')}
                                />
                            </Grid2>
                        </Box>

                        <FormSpy subscription={{ values: true }} onChange={handleChangeForm} />
                    </form>
                );
            }}
        />
    );
};

export default AddOriginForm;
